import DatabaseModel from './DatabaseModel.js';

/**
 * Adaptador de la cola de autorizaciones de OEE · FACTURACIÓN (#905) y del bloque «Condiciones de
 * facturación» de oferta y pedido.
 *
 * URLs verificadas contra AutorizacionController.java (@RequestMapping("/autorizaciones")).
 *
 * Quién puede autorizar, rechazar o pedir aclaración lo decide la API: la persona a la que se
 * asignó la solicitud al pedirla o el rol Administrador, y nunca quien la pidió; a cualquier otra,
 * 403. La pantalla solo esconde los botones.
 */
const Autorizaciones = Object.create(DatabaseModel);
Autorizaciones.model = 'autorizaciones';

/** La pantalla OEE › Autorizaciones de una vez: pendientes, resueltas, contadores y permiso. */
Autorizaciones.bandeja = function() {
    const self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/bandeja',
        method: 'GET',
        data: {}
    }, false);
};

Autorizaciones.autorizar = function(id, comentario) {
    return this.decidir(id, 'autorizar', comentario);
};

/** Rechazar exige motivo: la API se lo manda por correo a quien lo pidió. */
Autorizaciones.rechazar = function(id, motivo) {
    return this.decidir(id, 'rechazar', motivo);
};

/** La solicitud sigue pendiente; la pregunta va al historial y por correo a quien la pidió. */
Autorizaciones.pedirAclaracion = function(id, pregunta) {
    return this.decidir(id, 'aclaracion', pregunta);
};

Autorizaciones.decidir = function(id, accion, texto) {
    const self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/' + encodeURIComponent(id) + '/' + accion,
        method: 'PUT',
        contentType: 'application/json',
        data: { texto: texto || null }
    }, false);
};

/**
 * Cómo están las condiciones GUARDADAS de una oferta o pedido: pendientes, si están pedidas, si el
 * cliente es interno y quién autoriza. `documento` es 'OFERTA' o 'PEDIDO'.
 */
Autorizaciones.estadoCondiciones = function(documento, id) {
    const self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/condiciones/' + documento + '/' + encodeURIComponent(id),
        method: 'GET',
        data: {}
    }, false);
};

/** «Pedir autorización» de las condiciones guardadas que la necesitan, con un motivo. */
Autorizaciones.pedirCondiciones = function(documento, id, motivo) {
    const self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/condiciones/' + documento + '/' + encodeURIComponent(id),
        method: 'POST',
        contentType: 'application/json',
        data: { texto: motivo }
    }, false);
};

/**
 * Ajustes › Autorizaciones de condiciones de facturación: quién autoriza a quién (A), qué
 * condiciones requieren autorización (B), lo pendiente y a quién sigue asignado. Solo ADMIN y
 * DIRECTOR: a cualquier otro la API responde 403.
 */
Autorizaciones.configuracion = function() {
    const self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/configuracion',
        method: 'GET',
        data: {}
    }, false);
};

/**
 * Bloque A, en bloque como el mapa de aprobación de vacaciones. `accion`: 'ANADIR' o 'QUITAR' el
 * `autorizador` a las personas marcadas, o 'GENERALES' para dejarlas con los generales.
 * → { actualizados, sinCambios, errores[] }: no es todo o nada.
 */
Autorizaciones.asignarAutorizadores = function(usernames, accion, autorizador, motivo) {
    const self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/configuracion/personas',
        method: 'POST',
        contentType: 'application/json',
        data: { usernames: usernames, accion: accion, autorizador: autorizador || null, motivo: motivo || null }
    }, false);
};

/** Bloque B: si una condición requiere autorización y sus autorizadores propios. → la pantalla entera. */
Autorizaciones.guardarCondicion = function(clave, requiereAutorizacion, autorizadores) {
    const self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/configuracion/condiciones/' + encodeURIComponent(clave),
        method: 'PUT',
        contentType: 'application/json',
        data: { requiereAutorizacion: requiereAutorizacion, autorizadores: autorizadores || [] }
    }, false);
};

/**
 * Los autorizadores generales —los de quien no tiene a nadie asignado—, elegidos en Ajustes. La API
 * exige al menos una persona de alta. → la pantalla entera.
 */
Autorizaciones.guardarGenerales = function(usernames, motivo) {
    const self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/configuracion/generales',
        method: 'PUT',
        contentType: 'application/json',
        data: { autorizadores: usernames || [], motivo: motivo || null }
    }, false);
};

/**
 * Qué requiere autorización según Ajustes y quién se la autorizaría a quien está conectado:
 * [{ clave, requiereAutorizacion, autorizadores }]. Lo usa Pago y Finanzas para pintar el ámbar
 * antes de guardar, también en una oferta nueva.
 */
Autorizaciones.reglas = function() {
    const self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/configuracion/reglas',
        method: 'GET',
        data: {}
    }, false);
};

/**
 * #1142 · Cómo está la segunda a 0 € de una línea de una oferta o pedido GUARDADO: si es de un cliente
 * interno, si ya se pidió, si se puede pedir (y si no, por qué), quién la autoriza y dónde.
 * URL verificada contra SegundaACeroController.java (@RequestMapping("/autorizaciones/segunda-a-cero")).
 */
// La clave de la línea (la que dio la API al pedirla) es lo que distingue esta línea de otra que
// haya tomado su número; sin ella la API no aplica ninguna petición.
Autorizaciones.estadoSegundaACero = function(documento, id, numeroLinea, servicio, claveLinea) {
    const self = this;
    const params = [];
    if (servicio) params.push('servicio=' + encodeURIComponent(servicio));
    if (claveLinea) params.push('claveLinea=' + encodeURIComponent(claveLinea));
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/segunda-a-cero/' + documento + '/'
            + encodeURIComponent(id) + '/' + encodeURIComponent(numeroLinea)
            + (params.length ? '?' + params.join('&') : ''),
        method: 'GET',
        data: {}
    }, false);
};

/** #1142 · «Dejar a 0 € y pedir autorización» de esa línea, tal como la tiene la pantalla. */
Autorizaciones.pedirSegundaACero = function(documento, id, linea, motivo) {
    const self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/segunda-a-cero/' + documento + '/' + encodeURIComponent(id),
        method: 'POST',
        contentType: 'application/json',
        data: { linea: linea, motivo: motivo }
    }, false);
};

export default Autorizaciones;
